import React, { Fragment, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "components/Header";
import Footer from "components/Footer";
import useOrderDetailsQuery from "./order/useOrderDetailsQuery";
import "assets/css/App.css";
import "assets/css/index.css";
import { useUser } from "hooks"; // Custom hook for user data


export default function OrderDetailsPage() {
  const { id } = useParams();
  const { user } = useUser(); // Get user data from useUser hook

  const navigate = useNavigate();
  const { order, loading, error } = useOrderDetailsQuery(id);

  const getStatusText = useCallback((status) => {
    const statusMap = {
      1: "pending",
      2: "processing",
      3: "shipped",
      4: "completed",
      5: "cancelled",
    };
    return statusMap[status] || "unknown";
  }, []);

  const getStatusColor = useCallback((status) => {
    const colorMap = {
      completed: "success",
      shipped: "success",
      processing: "info",
      pending: "warning",
      cancelled: "danger",
    };
    return colorMap[status.toLowerCase()] || "secondary";
  }, []);
   
  if (!id) {
    return (
      <Fragment>
        <Header />
        <div className="container py-4 min-vh-100">
          <div className="alert alert-danger" role="alert">
            Invalid order ID
            <button
              className="btn btn-primary ms-3"
              onClick={() => navigate("/profile")}
            >
              Back to Orders
            </button>
          </div>
        </div>
        <Footer category={{}} />
      </Fragment>
    );
  }

  if (loading) {
    return (
      <Fragment>
        <Header />
        <div className="container py-4 min-vh-100">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
        <Footer category={{}} />
      </Fragment>
    );
  }


  if (error || !order) {
    return (
      <Fragment>
        <Header />
        <div className="container py-4 min-vh-100">
          <div className="alert alert-danger" role="alert">
            Error loading order: {error || "Order not found"}
            <button
              className="btn btn-primary ms-3"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
            <button
              className="btn btn-secondary ms-3"
              onClick={() => navigate("/profile")}
            >
              Back to Orders
            </button>
          </div>
        </div>
        <Footer category={{}} />
      </Fragment>
    );
  }

  // Parse JSON addresses
  const shippingAddress = order.shipping_address
    ? JSON.parse(order.shipping_address)
    : {};
  const billingAddress = order.billing_address
    ? JSON.parse(order.billing_address)
    : {};


  return (
    <Fragment>
      <Header />
      <div className="container py-4 min-vh-100">
        {/* Header Section */}
        <div className="order-header mb-4">
          <h2 className="h4">Order #{order.tracking_number || order.id}</h2>
        </div>

        {/* Main Content and Sidebar Layout */}
        <div className="row g-4">
          {/* Product Details Section */}
          <div className="col-lg-8">
            <div className="card mb-4">
              <div className="card-header bg-light">Product Details</div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-borderless">
                    <thead>
                      <tr className="text-muted">
                        <th>Product Details</th>
                        <th>Item Price</th>
                        <th>Quantity</th>
                        <th>Total Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.products &&
                        order.products.map((item, index) => (
                          <tr key={index}>
                            <td>
                              <img
                                src={
                                  "https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg" // Replace with actual image URL if available
                                }
                                alt={item.name}
                                className="me-3 img-thumbnail"
                                style={{ width: "50px" }}
                                onError={(e) =>
                                  (e.target.src = "https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg")
                                }
                              />
                              {item.name}
                              <br />
                            </td>
                            <td>₹{(item.unit_price || 0).toFixed(2)}</td>
                            <td>{item.quantity || 1}</td>

                            <td>₹{(item.subtotal || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Order Summary Section */}
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">Order Summary</h5>
                <div className="list-group list-group-flush">
                  <div className="list-group-item d-flex justify-content-between">
                    <span>Sub Total :</span>
                    <span>₹{(order.total || 0).toFixed(2)}</span>
                  </div>
                  <div className="list-group-item d-flex justify-content-between">
                    <span>Discount :</span>
                    <span className="text-danger">
                      ₹{(order.discount || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="list-group-item d-flex justify-content-between">
                    <span>Shipping Charge :</span>
                    <span>₹{(order.delivery_fee || 0).toFixed(2)}</span>
                  </div>
                  <div className="list-group-item d-flex justify-content-between">
                    <span>Estimated Tax :</span>
                    <span>₹{(order.sales_tax || 0).toFixed(2)}</span>
                  </div>
                  <div className="list-group-item d-flex justify-content-between fw-bold border-top">
                    <span>Total (₹) :</span>
                    <span>₹{(order.paid_total || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar Section */}
          <div className="col-lg-4">

            {/* Customer Details */}
            <div className="card mb-4">
              <div className="card-header bg-light">Customer Details</div>
              <div className="card-body text-center">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/9/99/Sample_User_Icon.png"
                  alt="Customer"
                  className="customer-image rounded-circle mb-2"
                  style={{ width: "50px", height: "50px" }}
                />
                <h6 className="mb-1">{user?.name}</h6>
                <p className="mb-1 text-muted">{shippingAddress.email}</p>
                <p className="mb-1 text-muted">{shippingAddress.phone}</p>
                <Link to="#" className="btn btn-link text-decoration-none">
                  View Profile
                </Link>
              </div>
            </div>

            {/* Billing Address */}
            <div className="card mb-4">
              <div className="card-header bg-light">Billing Address</div>
              <div className="card-body">
                <p className="mb-1">{billingAddress.email}</p>
                <p className="mb-1">{billingAddress.phone}</p>
                <p className="mb-1">{billingAddress.address}</p>
                <p className="mb-1">
                  {billingAddress.city}, {billingAddress.state} - {billingAddress.zip}
                </p>
                <p className="mb-0">{billingAddress.country}</p>
              </div>
            </div>
            <div className="card mb-4">
              <div className="card-header bg-light">Shipping Address</div>
              <div className="card-body">
                <p className="mb-1">{shippingAddress.email}</p>
                <p className="mb-1">{shippingAddress.phone}</p>
                <p className="mb-1">{shippingAddress.address}</p>
                <p className="mb-1">
                  {shippingAddress.city}, {shippingAddress.state} - {shippingAddress.zip}
                </p>
                <p className="mb-0">{shippingAddress.country}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer category={{}} />
    </Fragment>
  );
}

function SidebarCard({ title, content, children }) {
  return (
    <div className="card mb-3">
      <div className="card-header bg-light">{title}</div>
      <div className="card-body">{content || children}</div>
    </div>
  );
}